import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateAgent } from '@/lib/api/agents';
import { useModels } from '@/lib/api/models';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { useWorkspacePath } from '@/lib/workspacePath';

export function CreateAgentForm() {
  const navigate = useNavigate();
  const ws = useWorkspacePath();
  const createAgent = useCreateAgent();
  const { data: models, isLoading: modelsLoading } = useModels();

  const [name, setName] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [modelKey, setModelKey] = useState('');

  // Preselect the catalog's default entry once the list loads (FR-7); only
  // when the member hasn't already picked something themselves.
  useEffect(() => {
    if (modelKey || !models || models.length === 0) return;
    const preferred = models.find((m) => m.is_default) ?? models[0];
    setModelKey(preferred.key);
  }, [models, modelKey]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    createAgent.mutate(
      { name, system_prompt: systemPrompt, model_key: modelKey },
      {
        onSuccess: (agent) => {
          navigate(ws('agents', agent.slug, 'chat'));
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Support assistant"
        required
        data-testid="agent-name-input"
      />
      <div>
        <Select
          label="Model"
          value={modelKey}
          onChange={(e) => setModelKey(e.target.value)}
          required
          disabled={modelsLoading}
          data-testid="agent-model-select"
        >
          {!modelKey && <option value="">Select a model...</option>}
          {models?.map((m) => (
            <option key={m.key} value={m.key}>
              {m.display_name}
            </option>
          ))}
        </Select>
        {modelKey && (
          <p className="mt-1 text-xs text-gray-500" data-testid="agent-model-description">
            {models?.find((m) => m.key === modelKey)?.description}
          </p>
        )}
      </div>
      <Textarea
        label="System prompt"
        value={systemPrompt}
        onChange={(e) => setSystemPrompt(e.target.value)}
        placeholder="You are a helpful assistant that..."
        rows={5}
        required
        data-testid="agent-system-prompt-input"
      />

      {createAgent.error && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {(createAgent.error as Error).message}
        </p>
      )}

      <Button
        type="submit"
        disabled={createAgent.isPending || !modelKey}
        className="w-full"
        data-testid="create-agent-submit"
      >
        {createAgent.isPending ? 'Creating...' : 'Create agent'}
      </Button>
    </form>
  );
}
